import type { InternalAxiosRequestConfig } from 'axios';
import { refreshAuthenticationTokens } from '@/api/auth/authApi';
import type { AuthenticationTokens } from '@/api/auth/authApi.types';
import { readStoredRefreshToken, storeAuthenticationTokens } from '@/lib/authStorage';

export interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  hasRetriedAfterTokenRefresh?: boolean;
}

const AUTHENTICATION_ENDPOINTS_EXEMPT_FROM_REFRESH = ['/auth/login', '/auth/refresh'];

let sharedRefreshPromise: Promise<AuthenticationTokens> | null = null;

function isAuthenticationEndpointRequest(requestUrl: string | undefined): boolean {
  if (requestUrl === undefined) {
    return false;
  }
  return AUTHENTICATION_ENDPOINTS_EXEMPT_FROM_REFRESH.some(function matchesEndpoint(endpoint) {
    return requestUrl.includes(endpoint);
  });
}

export function shouldAttemptTokenRefresh(
  requestConfiguration: RetryableRequestConfig | undefined
): boolean {
  if (requestConfiguration === undefined) {
    return false;
  }
  if (requestConfiguration.hasRetriedAfterTokenRefresh === true) {
    return false;
  }
  if (isAuthenticationEndpointRequest(requestConfiguration.url) === true) {
    return false;
  }
  return readStoredRefreshToken().length > 0;
}

async function performTokenRefresh(): Promise<AuthenticationTokens> {
  const storedRefreshToken = readStoredRefreshToken();
  if (storedRefreshToken.length === 0) {
    throw new Error('No refresh token is stored.');
  }
  const refreshedTokens = await refreshAuthenticationTokens(storedRefreshToken);
  storeAuthenticationTokens(refreshedTokens.accessToken, refreshedTokens.refreshToken);
  return refreshedTokens;
}

export function refreshAuthenticationSession(): Promise<AuthenticationTokens> {
  if (sharedRefreshPromise !== null) {
    return sharedRefreshPromise;
  }
  sharedRefreshPromise = performTokenRefresh().finally(function clearSharedRefreshPromise() {
    sharedRefreshPromise = null;
  });
  return sharedRefreshPromise;
}
